import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Table, Nav, Form, Button, Badge,
  Dropdown, Spinner, OverlayTrigger, Tooltip, Alert,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import {
  fetchPracticeTemplates,
  getPracticesByNumbers,
  fetchTemplateLibrary,
  fetchLibraryTemplate,
  fetchTemplateById,
  setTemplateStatus,
  setLibraryTemplateStatus,
  promoteTemplateToLibrary,
} from "../../api";
import ViewTemplate from "./ViewTemplate";
import NoteRenderer from "./NoteRenderer";
import { TemplateBuilder } from "./TemplateBuilder";
import AdoptTemplateModal from "./AdoptTemplateModal";
import TemplateDeleteModal from "./TemplateDeleteModal";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import { isSystemAdmin as readIsSystemAdmin, canAdoptLibraryTemplates } from "../../utils/permissions";
import "../Accounts/acc-list.css";

/**
 * TemplateManagement — the Templates section of Practice Settings.
 *
 * Two tabs:
 *   Practice Templates — the practice's own templates, one data-driven table
 *                        across all four categories rather than a table per
 *                        category.
 *   Template Library   — system templates authored by a SystemAdmin. A practice
 *                        takes one by adopting it, which copies it in. There is
 *                        no link back: the copy is independent immediately.
 *
 * Visual conventions: /CLAUDE.md §1.2 layout, §1.6 tables (desktop table paired
 * with a `.acc-card` list under lg), §1.9 badges, §1.10 dropdowns, §1.11 icons.
 */

const CATEGORIES = [
  { key: "initial", label: "Initial Assessment" },
  { key: "followup", label: "Follow-Up" },
  { key: "reportexport", label: "Report Export" },
  { key: "intake", label: "Intake" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.key, c.label]));
const LANGUAGE_LABEL = { en: "English", af: "Afrikaans" };

function statusVariant(status) {
  if (status === "Active") return "success";
  if (status === "Inactive") return "secondary";
  return "warning"; // Draft
}

function StatusBadge({ status }) {
  return <Badge bg={statusVariant(status)}>{status || "Draft"}</Badge>;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
}

// templateDisciplines is [{ disciplineCode, disciplineName }]; empty or
// absent means the template applies to every discipline.
function disciplineNames(template) {
  const list = Array.isArray(template?.templateDisciplines) ? template.templateDisciplines : [];
  return list.map((d) => d.disciplineName || d.disciplineCode).filter(Boolean);
}

function DisciplineCell({ template, inline = false }) {
  const names = disciplineNames(template);
  if (names.length === 0) return <span className="text-muted">All disciplines</span>;
  if (inline) return <>{names.join(", ")}</>;
  return <>{names.map((n) => <div key={n}>{n}</div>)}</>;
}

export default function TemplateManagement({ refresh, onTemplateEdit }) {
  const { toast, showToast, hideToast } = useToast();

  const [tab, setTab] = useState("practice");
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  // Taking a library template into the practice is a SuperAdmin / SystemAdmin
  // action; everyone else can browse and preview only.
  const [canAdopt, setCanAdopt] = useState(false);
  const [disciplineFilter, setDisciplineFilter] = useState("all");

  const [practiceTemplates, setPracticeTemplates] = useState([]);
  const [practiceNames, setPracticeNames] = useState([]);
  const [loadingPractice, setLoadingPractice] = useState(true);
  const [practiceError, setPracticeError] = useState("");

  const [libraryTemplates, setLibraryTemplates] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState("");
  const [showDrafts, setShowDrafts] = useState(false);

  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [libraryPreview, setLibraryPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // One builder instance serves both tabs — `scope` tells it which routes to
  // save through.
  const [builder, setBuilder] = useState({ show: false, mode: "add", template: null, scope: "practice" });
  const [adopting, setAdopting] = useState(null);
  const [deleting, setDeleting] = useState(null); // { template, scope }

  useEffect(() => {
    setIsSystemAdmin(readIsSystemAdmin());
    setCanAdopt(canAdoptLibraryTemplates());
  }, []);

  const loadPracticeTemplates = useCallback(async () => {
    const token = sessionStorage.getItem("user");
    if (!token) return;
    setLoadingPractice(true);
    setPracticeError("");
    try {
      const decoded = jwtDecode(token);
      const [templates, practices] = await Promise.all([
        fetchPracticeTemplates(decoded._id),
        getPracticesByNumbers(decoded.practice),
      ]);
      setPracticeTemplates(Array.isArray(templates) ? templates : []);
      setPracticeNames(Array.isArray(practices) ? practices : []);
    } catch (err) {
      setPracticeError(err.message || "Failed to load templates.");
    } finally {
      setLoadingPractice(false);
    }
  }, []);

  const loadLibrary = useCallback(async () => {
    setLoadingLibrary(true);
    setLibraryError("");
    try {
      // Drafts are only ever returned to a SystemAdmin; asking for them as
      // anyone else simply returns the published set.
      const data = await fetchTemplateLibrary(isSystemAdmin && showDrafts);
      setLibraryTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setLibraryError(err.message || "Failed to load the template library.");
    } finally {
      setLoadingLibrary(false);
    }
  }, [isSystemAdmin, showDrafts]);

  useEffect(() => { loadPracticeTemplates(); }, [refresh, loadPracticeTemplates]);
  useEffect(() => { if (tab === "library") loadLibrary(); }, [tab, loadLibrary]);

  const practiceNameFor = useCallback(
    (practiceNumber) => {
      const match = practiceNames.find((p) => p.practiceNumber === practiceNumber);
      return match ? match.practiceName : "Unknown Practice";
    },
    [practiceNames],
  );

  const matchesFilters = useCallback(
    (t) => {
      if (category !== "all" && t.templateInitial !== category) return false;
      if (!search.trim()) return true;
      const needle = search.trim().toLowerCase();
      return (
        (t.templateTitle || "").toLowerCase().includes(needle) ||
        (t.templateDescription || "").toLowerCase().includes(needle)
      );
    },
    [category, search],
  );

  const visiblePractice = useMemo(
    () => practiceTemplates.filter(matchesFilters),
    [practiceTemplates, matchesFilters],
  );

  // Discipline choices for the library filter, drawn from what the server
  // returned: a practice user only ever receives their own discipline(s) plus
  // the all-discipline templates, a SystemAdmin receives everything.
  const libraryDisciplines = useMemo(() => {
    const byCode = new Map();
    for (const t of libraryTemplates) {
      for (const d of (t.templateDisciplines || [])) {
        if (d?.disciplineCode && !byCode.has(d.disciplineCode)) byCode.set(d.disciplineCode, d);
      }
    }
    return [...byCode.values()].sort((a, b) =>
      String(a.disciplineName || "").localeCompare(String(b.disciplineName || "")),
    );
  }, [libraryTemplates]);

  const visibleLibrary = useMemo(
    () => libraryTemplates.filter((t) => {
      if (!matchesFilters(t)) return false;
      if (disciplineFilter === "all") return true;
      const list = t.templateDisciplines || [];
      // "all-discipline" templates belong under every discipline choice.
      return list.length === 0 || list.some((d) => d.disciplineCode === disciplineFilter);
    }),
    [libraryTemplates, matchesFilters, disciplineFilter],
  );

  // ── Actions ────────────────────────────────────────────────────────────────

  const refreshAll = useCallback(() => {
    loadPracticeTemplates();
    if (tab === "library") loadLibrary();
    if (onTemplateEdit) onTemplateEdit();
  }, [loadPracticeTemplates, loadLibrary, tab, onTemplateEdit]);

  const handleStatusChange = async (template, nextStatus, scope) => {
    try {
      if (scope === "system") await setLibraryTemplateStatus(String(template._id), nextStatus);
      else await setTemplateStatus(String(template._id), nextStatus);
      showToast("success", `${template.templateTitle} is now ${nextStatus}.`);
      refreshAll();
    } catch (err) {
      showToast("error", err.message || "Failed to update the template status.");
    }
  };

  const handlePromote = async (template) => {
    try {
      await promoteTemplateToLibrary(String(template._id));
      showToast("success", `${template.templateTitle} copied to the library as a Draft.`);
      if (tab === "library") loadLibrary();
    } catch (err) {
      showToast("error", err.message || "Failed to promote the template.");
    }
  };

  const openEditor = async (template, scope) => {
    if (scope === "system") {
      // The list projection has no pages[], so fetch the full document before
      // handing it to the builder.
      try {
        const full = await fetchLibraryTemplate(String(template._id));
        setBuilder({ show: true, mode: "edit", template: full, scope: "system" });
      } catch (err) {
        showToast("error", err.message || "Failed to open the template.");
      }
      return;
    }
    setBuilder({ show: true, mode: "edit", template, scope: "practice" });
  };

  const openLibraryPreview = async (template) => {
    setLoadingPreview(true);
    setLibraryPreview(null);
    try {
      const full = await fetchLibraryTemplate(String(template._id));
      setLibraryPreview(full);
    } catch (err) {
      showToast("error", err.message || "Failed to load the preview.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleAdopted = async (result) => {
    setAdopting(null);
    showToast("success", `${result.templateTitle} added to your templates as a Draft.`);
    await loadPracticeTemplates();
    if (onTemplateEdit) onTemplateEdit();
    setTab("practice");

    // Taking a template is almost always followed by changing it, so open the
    // copy in the builder rather than leaving the user to find it in the list.
    // A failure here is not worth a toast — the copy exists either way and is
    // sitting in the practice list behind this screen.
    try {
      const copy = await fetchTemplateById(String(result.templateId));
      if (copy && copy._id) {
        setBuilder({ show: true, mode: "edit", template: copy, scope: "practice" });
      }
    } catch {
      /* the adopted copy is already in the list; editing it is one click away */
    }
  };

  const handleDeleted = (template) => {
    const wasSelected = selectedTemplate && String(selectedTemplate._id) === String(template._id);
    setDeleting(null);
    if (wasSelected) setSelectedTemplate(null);
    if (libraryPreview && String(libraryPreview._id) === String(template._id)) setLibraryPreview(null);
    showToast("success", `${template.templateTitle} deleted.`);
    refreshAll();
  };

  // ── Shared bits ────────────────────────────────────────────────────────────

  const filterBar = (
    <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
      <Form.Group style={{ minWidth: 200 }}>
        <Form.Label>Category</Form.Label>
        <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="flex-fill" style={{ minWidth: 200 }}>
        <Form.Label>Search</Form.Label>
        <Form.Control
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Template name or description"
        />
      </Form.Group>

      {tab === "library" && libraryDisciplines.length > 0 && (
        <Form.Group style={{ minWidth: 200 }}>
          <Form.Label>Discipline</Form.Label>
          <Form.Select value={disciplineFilter} onChange={(e) => setDisciplineFilter(e.target.value)}>
            <option value="all">All disciplines</option>
            {libraryDisciplines.map((d) => (
              <option key={d.disciplineCode} value={d.disciplineCode}>
                {d.disciplineName || d.disciplineCode}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      {(category !== "all" || search || (tab === "library" && disciplineFilter !== "all")) && (
        <Button
          variant="outline-danger"
          onClick={() => { setCategory("all"); setSearch(""); setDisciplineFilter("all"); }}
        >
          <i className="fa-solid fa-trash me-2" />
          Clear filters
        </Button>
      )}
    </div>
  );

  /**
   * Desktop row actions — icon-only buttons, NOT a kebab dropdown.
   *
   * `.acc-list .table-responsive` sets `overflow: hidden` to clip its rounded
   * corners, which also clips any dropdown menu opened inside a row. The kebab
   * pattern works in `.acc-card` because that class deliberately omits
   * overflow:hidden for exactly this reason — it does not translate to a table.
   *
   * `popperConfig={{ strategy: "fixed" }}` escapes the clip but was removed in
   * Phase 37.1 after UAT found it positioning menus at the top of the page in
   * Chrome, so it is off the table. Icon buttons with tooltips are what
   * /CLAUDE.md §1.4 prescribes for actions inside a table row anyway.
   */
  const rowActionButtons = (template, scope) => {
    const isActive = template.templateStatus === "Active";
    const iconButton = (key, tooltip, icon, onClick, danger = false) => (
      <OverlayTrigger key={key} placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
        <Button
          variant="link"
          className={`${danger ? "text-danger" : "text-primary"} text-decoration-none p-0`}
          style={{ width: 24, height: 24 }}
          aria-label={tooltip}
          onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
          <i className={icon} />
        </Button>
      </OverlayTrigger>
    );

    return (
      <div
        className="d-flex gap-3 justify-content-center align-items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {iconButton("edit", "Edit", "fa-solid fa-pen-to-square", () => openEditor(template, scope))}
        {iconButton(
          "status",
          isActive ? "Retire" : "Publish",
          `fa-solid ${isActive ? "fa-eye-slash" : "fa-check"}`,
          () => handleStatusChange(template, isActive ? "Inactive" : "Active", scope),
        )}
        {scope === "practice" && isSystemAdmin &&
          iconButton("promote", "Copy to library", "fa-solid fa-file-export", () => handlePromote(template))}
        {iconButton("delete", "Delete", "fa-solid fa-trash", () => setDeleting({ template, scope }), true)}
      </div>
    );
  };

  /** Mobile row actions — a kebab, which `.acc-card` supports without clipping. */
  const rowActions = (template, scope) => {
    const isActive = template.templateStatus === "Active";
    return (
      <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
        <Dropdown.Toggle
          variant="link"
          className="acc-card-kebab text-primary text-decoration-none p-0 border-0"
          aria-label="Template actions"
        >
          <i className="fa-solid fa-ellipsis-vertical" />
        </Dropdown.Toggle>
        <Dropdown.Menu align="end" className="shadow-sm">
          <Dropdown.Item onClick={() => openEditor(template, scope)}>
            <i className="fa-solid fa-pen-to-square me-2" />
            Edit
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleStatusChange(template, isActive ? "Inactive" : "Active", scope)}>
            <i className={`fa-solid ${isActive ? "fa-eye-slash" : "fa-check"} me-2`} />
            {isActive ? "Retire" : "Publish"}
          </Dropdown.Item>
          {scope === "practice" && isSystemAdmin && (
            <Dropdown.Item onClick={() => handlePromote(template)}>
              <i className="fa-solid fa-file-export me-2" />
              Copy to library
            </Dropdown.Item>
          )}
          <Dropdown.Divider />
          <Dropdown.Item className="text-danger" onClick={() => setDeleting({ template, scope })}>
            <i className="fa-solid fa-trash me-2" />
            Delete
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const loadingBlock = (what) => (
    <div className="text-center py-5">
      <Spinner animation="border" variant="primary" />
      <p className="mt-2">Loading {what}…</p>
    </div>
  );

  // ── Practice tab ───────────────────────────────────────────────────────────

  const practiceTab = (
    <>
      {filterBar}

      {practiceError && <Alert variant="danger" className="py-2">{practiceError}</Alert>}

      {loadingPractice ? (
        loadingBlock("templates")
      ) : (
        <Row>
          <Col xs={12} xl={selectedTemplate ? 7 : 12}>
            {visiblePractice.length === 0 ? (
              <p className="mb-0">
                No templates found.
                {practiceTemplates.length > 0
                  ? " Adjust the filters to see more."
                  : " Add one, or take a copy from the Template Library."}
              </p>
            ) : (
              <>
                {/* Desktop */}
                <div className="d-none d-lg-block">
                  <Table bordered hover responsive>
                    <thead className="text-center">
                      <tr>
                        <th className="text-center align-middle text-primary">Title</th>
                        <th className="text-center align-middle text-primary">Category</th>
                        <th className="text-center align-middle text-primary">Discipline</th>
                        <th className="text-center align-middle text-primary">Status</th>
                        <th className="text-center align-middle text-primary">Language</th>
                        <th className="text-center align-middle text-primary">Practice</th>
                        <th className="text-center align-middle text-primary" style={{ width: 140 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visiblePractice.map((t) => (
                        <tr
                          key={t._id}
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setSelectedTemplate({
                              ...t,
                              practiceNames: (t.templatePracticeId || []).map(practiceNameFor),
                            })
                          }
                        >
                          <td>{t.templateTitle}</td>
                          <td>{CATEGORY_LABEL[t.templateInitial] || t.templateInitial}</td>
                          <td><DisciplineCell template={t} /></td>
                          <td className="text-center"><StatusBadge status={t.templateStatus} /></td>
                          <td>{LANGUAGE_LABEL[t.templateLanguage] || "English"}</td>
                          <td>
                            {(t.templatePracticeId || []).map((p) => (
                              <div key={p}>{practiceNameFor(p)}</div>
                            ))}
                          </td>
                          <td className="text-center">{rowActionButtons(t, "practice")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Mobile */}
                <div className="d-lg-none">
                  {visiblePractice.map((t) => (
                    <div
                      className="acc-card"
                      key={t._id}
                      onClick={() =>
                        setSelectedTemplate({
                          ...t,
                          practiceNames: (t.templatePracticeId || []).map(practiceNameFor),
                        })
                      }
                    >
                      <div className="acc-card-top">
                        <span className="acc-card-id">{t.templateTitle}</span>
                        <StatusBadge status={t.templateStatus} />
                        {rowActions(t, "practice")}
                      </div>
                      <div className="acc-card-body">
                        <div className="acc-card-field">
                          <span className="acc-card-label">Category</span>
                          <span className="acc-card-value">
                            {CATEGORY_LABEL[t.templateInitial] || t.templateInitial}
                          </span>
                        </div>
                        <div className="acc-card-field">
                          <span className="acc-card-label">Discipline</span>
                          <span className="acc-card-value"><DisciplineCell template={t} inline /></span>
                        </div>
                        <div className="acc-card-field">
                          <span className="acc-card-label">Language</span>
                          <span className="acc-card-value">
                            {LANGUAGE_LABEL[t.templateLanguage] || "English"}
                          </span>
                        </div>
                        <div className="acc-card-field">
                          <span className="acc-card-label">Practice</span>
                          <span className="acc-card-value">
                            {(t.templatePracticeId || []).map(practiceNameFor).join(", ")}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Col>

          {selectedTemplate && (
            <Col xs={12} xl={5}>
              <div className="border rounded p-3 mt-3 mt-xl-0">
                <div className="d-flex justify-content-end">
                  <OverlayTrigger placement="top" overlay={<Tooltip>Close preview</Tooltip>}>
                    <Button
                      variant="link"
                      className="text-primary text-decoration-none p-0"
                      style={{ width: 24, height: 24 }}
                      onClick={() => setSelectedTemplate(null)}
                    >
                      <i className="fa-solid fa-xmark" />
                    </Button>
                  </OverlayTrigger>
                </div>
                <ViewTemplate
                  selectedTemplate={selectedTemplate}
                  onTemplateEdit={refreshAll}
                  refresh={refresh}
                  editedTemplate={setSelectedTemplate}
                />
              </div>
            </Col>
          )}
        </Row>
      )}
    </>
  );

  // ── Library tab ────────────────────────────────────────────────────────────

  const libraryTab = (
    <>
      <div className="posted-invoice-info mb-3">
        <p className={canAdopt ? "mb-0" : "mb-1"}>
          Ready-made templates for your discipline that you can take and change for yourself.
          Taking one makes your own copy. Later changes to the library version will not affect it.
        </p>
        {!canAdopt && (
          <p className="mb-0 small text-muted">
            Only a practice SuperAdmin can add a library template to the practice. You can preview them here.
          </p>
        )}
      </div>

      {filterBar}

      {isSystemAdmin && (
        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <Button
            variant="primary"
            onClick={() => setBuilder({ show: true, mode: "add", template: null, scope: "system" })}
          >
            <i className="fa-solid fa-plus me-2" />
            New library template
          </Button>
          <Form.Check
            type="switch"
            id="library-show-drafts"
            label="Show drafts and retired"
            checked={showDrafts}
            onChange={(e) => setShowDrafts(e.target.checked)}
          />
        </div>
      )}

      {libraryError && <Alert variant="danger" className="py-2">{libraryError}</Alert>}

      {loadingLibrary ? (
        loadingBlock("the library")
      ) : (
        <Row>
          <Col xs={12} xl={libraryPreview || loadingPreview ? 7 : 12}>
            {visibleLibrary.length === 0 ? (
              <p className="mb-0">
                No library templates found.
                {libraryTemplates.length > 0 ? " Adjust the filters to see more." : ""}
              </p>
            ) : (
              <>
                {/* Desktop */}
                <div className="d-none d-lg-block">
                  <Table bordered hover responsive>
                    <thead className="text-center">
                      <tr>
                        <th className="text-center align-middle text-primary">Title</th>
                        <th className="text-center align-middle text-primary">Category</th>
                        <th className="text-center align-middle text-primary">Discipline</th>
                        <th className="text-center align-middle text-primary">Language</th>
                        {isSystemAdmin && (
                          <th className="text-center align-middle text-primary">Status</th>
                        )}
                        <th className="text-center align-middle text-primary">Updated</th>
                        <th className="text-center align-middle text-primary" style={{ width: 260 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLibrary.map((t) => (
                        <tr key={t._id} style={{ cursor: "pointer" }} onClick={() => openLibraryPreview(t)}>
                          <td>
                            {t.templateTitle}
                            {t.templateDescription && (
                              <div className="small text-muted">{t.templateDescription}</div>
                            )}
                          </td>
                          <td>{CATEGORY_LABEL[t.templateInitial] || t.templateInitial}</td>
                          <td><DisciplineCell template={t} /></td>
                          <td>{LANGUAGE_LABEL[t.templateLanguage] || "English"}</td>
                          {isSystemAdmin && (
                            <td className="text-center"><StatusBadge status={t.templateStatus} /></td>
                          )}
                          <td className="text-center">
                            {formatDate(t.templateUpdatedDate || t.templateCreatedDate)}
                          </td>
                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="d-flex gap-2 justify-content-center align-items-center">
                              <Button variant="outline-secondary" size="sm" onClick={() => openLibraryPreview(t)}>
                                Preview
                              </Button>
                              {canAdopt && (
                                <Button variant="primary" size="sm" onClick={() => setAdopting(t)}>
                                  Use template
                                </Button>
                              )}
                              {isSystemAdmin && rowActionButtons(t, "system")}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Mobile */}
                <div className="d-lg-none">
                  {visibleLibrary.map((t) => (
                    <div className="acc-card" key={t._id}>
                      <div className="acc-card-top">
                        <span className="acc-card-id">{t.templateTitle}</span>
                        {isSystemAdmin && <StatusBadge status={t.templateStatus} />}
                        {isSystemAdmin && rowActions(t, "system")}
                      </div>
                      <div className="acc-card-body">
                        <div className="acc-card-field">
                          <span className="acc-card-label">Category</span>
                          <span className="acc-card-value">
                            {CATEGORY_LABEL[t.templateInitial] || t.templateInitial}
                          </span>
                        </div>
                        <div className="acc-card-field">
                          <span className="acc-card-label">Discipline</span>
                          <span className="acc-card-value"><DisciplineCell template={t} inline /></span>
                        </div>
                        <div className="acc-card-field">
                          <span className="acc-card-label">Language</span>
                          <span className="acc-card-value">
                            {LANGUAGE_LABEL[t.templateLanguage] || "English"}
                          </span>
                        </div>
                        {t.templateDescription && (
                          <div className="acc-card-field">
                            <span className="acc-card-label">About</span>
                            <span className="acc-card-value">{t.templateDescription}</span>
                          </div>
                        )}
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <Button variant="outline-secondary" size="sm" onClick={() => openLibraryPreview(t)}>
                          Preview
                        </Button>
                        {canAdopt && (
                          <Button variant="primary" size="sm" onClick={() => setAdopting(t)}>
                            Use template
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Col>

          {(libraryPreview || loadingPreview) && (
            <Col xs={12} xl={5}>
              <div className="border rounded p-3 mt-3 mt-xl-0">
                {loadingPreview ? (
                  loadingBlock("preview")
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 text-primary fw-bold">{libraryPreview.templateTitle}</h6>
                      <OverlayTrigger placement="top" overlay={<Tooltip>Close preview</Tooltip>}>
                        <Button
                          variant="link"
                          className="text-primary text-decoration-none p-0"
                          style={{ width: 24, height: 24 }}
                          onClick={() => setLibraryPreview(null)}
                        >
                          <i className="fa-solid fa-xmark" />
                        </Button>
                      </OverlayTrigger>
                    </div>
                    <p className="small text-muted mb-2">
                      <DisciplineCell template={libraryPreview} inline />
                    </p>
                    {canAdopt && (
                      <Button variant="primary" size="sm" className="mb-3" onClick={() => setAdopting(libraryPreview)}>
                        <i className="fa-solid fa-copy me-2" />
                        Use this template
                      </Button>
                    )}
                    <NoteRenderer pages={libraryPreview.pages || []} mode="readonly" />
                  </>
                )}
              </div>
            </Col>
          )}
        </Row>
      )}
    </>
  );

  return (
    <Container fluid className="pt-2 acc-list">
      <Nav
        variant="pills"
        activeKey={tab}
        onSelect={(key) => key && setTab(key)}
        className="mb-3 gap-2"
      >
        <Nav.Item>
          <Nav.Link eventKey="practice">
            <i className="fa-sharp fa-light fa-file-lines me-2" />
            Practice Templates
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="library">
            <i className="fa-solid fa-book me-2" />
            Template Library
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {tab === "practice" ? practiceTab : libraryTab}

      <TemplateBuilder
        mode={builder.mode}
        template={builder.template}
        scope={builder.scope}
        show={builder.show}
        onHide={() => setBuilder((b) => ({ ...b, show: false }))}
        onSaved={() => {
          setBuilder((b) => ({ ...b, show: false }));
          refreshAll();
          if (tab === "library") loadLibrary();
        }}
      />

      <AdoptTemplateModal
        show={!!adopting}
        libraryTemplate={adopting}
        onHide={() => setAdopting(null)}
        onAdopted={handleAdopted}
      />

      <TemplateDeleteModal
        show={!!deleting}
        template={deleting?.template}
        scope={deleting?.scope}
        onHide={() => setDeleting(null)}
        onDeleted={handleDeleted}
      />

      <AppToast show={toast.show} onClose={hideToast} variant={toast.variant} message={toast.message} />
    </Container>
  );
}
